import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaCarSide,
  FaEuroSign,
  FaCalendarAlt,
  FaPercentage,
  FaPlus,
} from "react-icons/fa";
import { FaTruckFast } from "react-icons/fa6";
import moment from "moment";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import CommissionReport from "../components/PartnerManagement/CommissionReport";
import CreatePartnerRentalModal from "../components/PartnerManagement/CreatePartnerRentalModal";
import TransferModal from "../components/TransferModal/TransferModal";
import api from "../services/api";

const PartnerDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [rentals, setRentals] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [partner, setPartner] = useState(null);
  const [isCreateRentalModalOpen, setIsCreateRentalModalOpen] = useState(false);
  const [isCreateTransferModalOpen, setIsCreateTransferModalOpen] =
    useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("partnerToken");
      if (!token) {
        navigate("/partner/login");
        return;
      }

      try {
        const response = await api.auth.getCurrentUser();
        if (!response.data.user.isProfessionalPartner) {
          navigate("/partner/login");
          return;
        }
        setPartner(response.data.user);
        fetchPartnerRentals(response.data.user._id);
        fetchPartnerTransfers(response.data.user._id);
      } catch (error) {
        console.error("Error checking authentication:", error);
        navigate("/partner/login");
      }
    };

    checkAuth();
  }, [navigate]);
  const fetchPartnerRentals = async (partnerId) => {
    try {
      const response = await api.rentals.getByPartner(partnerId);
      setRentals(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching rentals:", error);
      setLoading(false);
    }
  };

  const fetchPartnerTransfers = async (partnerId) => {
    try {
      const response = await api.contact.getPartnerTransfers(partnerId);
      setTransfers(response.data);
    } catch (error) {
      console.error("Error fetching transfers:", error);
    }
  };
  const calculateStats = () => {
    const currentMonth = moment().format("YYYY-MM");
    const monthlyRentals = rentals.filter(
      (rental) => moment(rental.createdAt).format("YYYY-MM") === currentMonth
    );

    const monthlyTransfers = transfers.filter(
      (transfer) =>
        moment(transfer.createdAt).format("YYYY-MM") === currentMonth
    );

    const totalCommissionRentals = rentals.reduce(
      (sum, rental) => sum + (rental.commissionAmount || 0),
      0
    );
    const totalCommissionTransfers = transfers.reduce(
      (sum, transfer) => sum + (transfer.partnerCommission || 0),
      0
    );
    const monthlyCommissionRentals = monthlyRentals.reduce(
      (sum, rental) => sum + (rental.commissionAmount || 0),
      0
    );
    const monthlyCommissionTransfers = monthlyTransfers.reduce(
      (sum, transfer) => sum + (transfer.partnerCommission || 0),
      0
    );

    return {
      totalRentals: rentals.length,
      totalTransfers: transfers.length,
      monthlyRentals: monthlyRentals.length,
      monthlyTransfers: monthlyTransfers.length,
      totalCommission: totalCommissionRentals + totalCommissionTransfers,
      monthlyCommission: monthlyCommissionRentals + monthlyCommissionTransfers,
      averageCommission:
        rentals.length + transfers.length
          ? (totalCommissionRentals + totalCommissionTransfers) /
            (rentals.length + transfers.length)
          : 0,
    };
  };
  const handleRentalCreated = (rental) => {
    // Add the new rental to the list and refresh data
    fetchPartnerRentals(partner._id);
  };

  const handleTransferCreated = (transfer) => {
    // Add the new transfer to the list and refresh data
    fetchPartnerTransfers(partner._id);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const stats = calculateStats();

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-t from-gray-300 via-white to-slate-800">
      <Header />
      <div className="flex-grow container mx-auto px-4 py-12">
      <div className="flex justify-between items-center mb-8 bg-gradient-to-r from-gray-600 to-gray-800 p-4 rounded-lg shadow-md">
      <div className="">
            <h1 className="text-3xl font-bold text-gray-200">
              Partner Dashboard
            </h1>
            <p className="text-gray-300">
              Welcome back, {partner?.firstName} {partner?.lastName}
            </p>
          </div>
          <div className="flex space-x-4">
            <motion.button
              onClick={() => setIsCreateRentalModalOpen(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors duration-300 flex items-center space-x-2"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FaPlus className="w-4 h-4" />
              <span>Create Rental</span>
            </motion.button>

            <motion.button
              onClick={() => setIsCreateTransferModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-300 flex items-center space-x-2"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              <FaPlus className="w-4 h-4" />
              <span>Create Transfer</span>
            </motion.button>
          </div>
        </div>
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-r from-gray-600 to-gray-800 p-6 rounded-lg shadow-md"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-300">Total Rentals</p>
                <p className="text-2xl font-bold text-gray-200">
                  {stats.totalRentals}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FaCarSide className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-r from-gray-600 to-gray-800 p-6 rounded-lg shadow-md"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-300">Total Transfers</p>
                <p className="text-2xl font-bold text-gray-200">
                  {stats.totalTransfers}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <FaCalendarAlt className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-r from-gray-600 to-gray-800 p-6 rounded-lg shadow-md"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-300">Total Commission</p>
                <p className="text-2xl font-bold text-gray-200">
                  €{stats.totalCommission.toFixed(2)}
                </p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <FaEuroSign className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-r from-gray-600 to-gray-800 p-6 rounded-lg shadow-md"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-300">Commission Rate</p>
                <p className="text-2xl font-bold text-gray-200">
                  {partner?.commissionRate}%
                </p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <FaPercentage className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </motion.div>
        </div>{" "}
        {/* Commission Report */}
        {partner && (
          <CommissionReport
            rentals={rentals}
            transfers={transfers}
            partner={partner}
            onRentalsUpdate={() => fetchPartnerRentals(partner._id)}
          />
        )}
        {/* CreatePartnerRentalModal */}
        <CreatePartnerRentalModal
          isOpen={isCreateRentalModalOpen}
          onClose={() => setIsCreateRentalModalOpen(false)}
          partner={partner}
          onSuccess={handleRentalCreated}
        />
        {/* TransferModal */}
        <TransferModal
          isOpen={isCreateTransferModalOpen}
          onClose={() => setIsCreateTransferModalOpen(false)}
          partnerId={partner?._id}
          partnerCommissionRate={partner?.commissionRate}
          onSuccess={handleTransferCreated}
        />
      </div>
      <Footer />
    </div>
  );
};

export default PartnerDashboard;
